import { useAuthor } from "@/hooks/useAuthor";
import { useLeaderboard } from "@/hooks/useLeaderboard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { ScoreEntry } from "@/game/leaderboard";
import { formatSats } from "@/game/format";
import { DIFFICULTIES } from "@/game/data";
import type { Difficulty } from "@/game/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Trophy } from "lucide-react";

function shortNpub(pubkey: string): string {
  return `pioneer_${pubkey.slice(0, 6)}`;
}

function difficultyLabel(difficulty: string | null): string {
  if (!difficulty) return "";
  const d = DIFFICULTIES[difficulty as Difficulty];
  return d ? `${d.emoji} ${d.label} · ` : "";
}

function medal(rank: number): string {
  if (rank === 0) return "🥇";
  if (rank === 1) return "🥈";
  if (rank === 2) return "🥉";
  return `#${rank + 1}`;
}

function LeaderboardRow({
  entry,
  rank,
  highlight,
}: {
  entry: ScoreEntry;
  rank: number;
  highlight: boolean;
}) {
  const author = useAuthor(entry.pubkey);
  const name = author.data?.metadata?.name ?? shortNpub(entry.pubkey);
  const picture = author.data?.metadata?.picture;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md border px-3 py-2",
        highlight ? "border-primary bg-primary/10" : "border-border bg-card",
      )}
    >
      <div className="w-8 shrink-0 text-center font-pixel text-xs">
        {medal(rank)}
      </div>
      <Avatar className="size-8 shrink-0">
        <AvatarImage src={picture} alt={name} />
        <AvatarFallback className="text-xs">
          {name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="truncate font-terminal text-lg leading-tight">
          {name}
          {highlight && (
            <span className="ml-2 font-pixel text-[8px] text-primary">YOU</span>
          )}
        </div>
        <div className="font-terminal text-sm text-muted-foreground leading-tight">
          {entry.outcome === "victory" ? "🎉 " : "🪦 "}
          {difficultyLabel(entry.difficulty)}
          {entry.days}d · {formatSats(entry.sats)} sats
          {entry.survivors != null && entry.party != null
            ? ` · ${entry.survivors}/${entry.party} survived`
            : ""}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="font-pixel text-xs text-primary">
          {formatSats(entry.score)}
        </div>
        <Badge
          variant={entry.outcome === "victory" ? "default" : "secondary"}
          className="mt-1 font-terminal text-xs"
        >
          {entry.outcome === "victory" ? "Freedom" : "Fell"}
        </Badge>
      </div>
    </div>
  );
}

export function Leaderboard({
  victoriesOnly = false,
  className,
}: {
  victoriesOnly?: boolean;
  className?: string;
}) {
  const { data: entries, isLoading, isError } = useLeaderboard({ victoriesOnly });
  const { user } = useCurrentUser();

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-2">
        <Trophy className="size-5 text-primary" />
        <h3 className="font-pixel text-sm text-primary">Global Leaderboard</h3>
      </div>
      <p className="font-terminal text-base text-muted-foreground">
        Every score lives on Nostr — a permissionless, decentralized hall of fame.
      </p>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 rounded-md border bg-card px-3 py-2">
              <Skeleton className="size-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-40" />
              </div>
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
      )}

      {isError && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <p className="font-terminal text-lg text-muted-foreground">
              Couldn't reach the relays. Check your connection and try again.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && entries && entries.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <p className="font-terminal text-lg text-muted-foreground">
              No scores yet. Finish a run and be the first pioneer on the board!
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && entries && entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry, i) => (
            <LeaderboardRow
              key={entry.id}
              entry={entry}
              rank={i}
              highlight={!!user && user.pubkey === entry.pubkey}
            />
          ))}
        </div>
      )}
    </div>
  );
}
